import { toPng, toJpeg } from 'html-to-image'

// ============================================================
// 类型定义
// ============================================================

export interface CollageOptions {
  width?: number
  height?: number
  quality?: number
  format?: 'png' | 'jpeg'
  gap?: number
  backgroundColor?: string
}

export type CollageLayout = 
  | 'single'      // 单图
  | '2x1'         // 横向2图
  | '1x2'         // 竖向2图
  | '2x2'         // 四宫格
  | '3x1'         // 横向3图
  | '1x3'         // 竖向3图
  | '3x3'         // 九宫格
  | '1+2'         // 1大图 + 2小图
  | '2+1'         // 2小图 + 1大图

export interface CollageConfig {
  layout: CollageLayout
  images: string[]  // 图片 URL 数组
  options?: CollageOptions
}

// 小红书标准尺寸
export const XHS_SIZES = {
  portrait: { width: 1080, height: 1440 },  // 3:4 竖版
  square: { width: 1080, height: 1080 },    // 1:1 方形
  landscape: { width: 1440, height: 1080 }, // 4:3 横版
}

// ============================================================
// 拼图布局配置
// ============================================================

interface LayoutConfig {
  cols: number
  rows: number
  cells: Array<{
    col: number
    row: number
    colSpan?: number
    rowSpan?: number
  }>
}

const LAYOUT_CONFIGS: Record<CollageLayout, LayoutConfig> = {
  'single': { cols: 1, rows: 1, cells: [{ col: 0, row: 0 }] },
  '2x1': { cols: 2, rows: 1, cells: [{ col: 0, row: 0 }, { col: 1, row: 0 }] },
  '1x2': { cols: 1, rows: 2, cells: [{ col: 0, row: 0 }, { col: 0, row: 1 }] },
  '2x2': { 
    cols: 2, rows: 2, 
    cells: [
      { col: 0, row: 0 }, { col: 1, row: 0 },
      { col: 0, row: 1 }, { col: 1, row: 1 }
    ] 
  },
  '3x1': { 
    cols: 3, rows: 1, 
    cells: [{ col: 0, row: 0 }, { col: 1, row: 0 }, { col: 2, row: 0 }] 
  },
  '1x3': { 
    cols: 1, rows: 3, 
    cells: [{ col: 0, row: 0 }, { col: 0, row: 1 }, { col: 0, row: 2 }] 
  },
  '3x3': { 
    cols: 3, rows: 3, 
    cells: [
      { col: 0, row: 0 }, { col: 1, row: 0 }, { col: 2, row: 0 },
      { col: 0, row: 1 }, { col: 1, row: 1 }, { col: 2, row: 1 },
      { col: 0, row: 2 }, { col: 1, row: 2 }, { col: 2, row: 2 }
    ] 
  },
  '1+2': { 
    cols: 2, rows: 2, 
    cells: [
      { col: 0, row: 0, rowSpan: 2 },  // 左边大图
      { col: 1, row: 0 },               // 右上
      { col: 1, row: 1 }                // 右下
    ] 
  },
  '2+1': { 
    cols: 2, rows: 2, 
    cells: [
      { col: 0, row: 0 },               // 左上
      { col: 0, row: 1 },               // 左下
      { col: 1, row: 0, rowSpan: 2 }   // 右边大图
    ] 
  },
}

// ============================================================
// 核心功能
// ============================================================

/**
 * 根据布局获取所需图片数量
 */
export function getRequiredImageCount(layout: CollageLayout): number {
  return LAYOUT_CONFIGS[layout].cells.length
}

/**
 * 创建拼图容器 HTML
 * 返回用于渲染的 HTML 字符串
 */
export function createCollageHTML(config: CollageConfig): string {
  const { layout, images, options = {} } = config
  const { 
    width = XHS_SIZES.portrait.width, 
    height = XHS_SIZES.portrait.height,
    gap = 4,
    backgroundColor = '#ffffff'
  } = options

  const layoutConfig = LAYOUT_CONFIGS[layout]
  const cellWidth = (width - gap * (layoutConfig.cols + 1)) / layoutConfig.cols
  const cellHeight = (height - gap * (layoutConfig.rows + 1)) / layoutConfig.rows

  let cellsHTML = ''
  layoutConfig.cells.forEach((cell, index) => {
    if (index >= images.length) return

    const imgUrl = images[index]
    // 添加时间戳避开缓存，强制触发 CORS 验证
    const cacheBuster = `${imgUrl}${imgUrl.includes('?') ? '&' : '?'}t=${Date.now()}`
    
    const colSpan = cell.colSpan || 1
    const rowSpan = cell.rowSpan || 1
    const x = gap + cell.col * (cellWidth + gap)
    const y = gap + cell.row * (cellHeight + gap)
    const w = cellWidth * colSpan + gap * (colSpan - 1)
    const h = cellHeight * rowSpan + gap * (rowSpan - 1)

    cellsHTML += `
      <div style="
        position: absolute;
        left: ${x}px;
        top: ${y}px;
        width: ${w}px;
        height: ${h}px;
        overflow: hidden;
        border-radius: 8px;
      ">
        <img 
          src="${cacheBuster}" 
          style="
            width: 100%;
            height: 100%;
            object-fit: cover;
          "
          crossorigin="anonymous"
        />
      </div>
    `
  })

  return `
    <div id="collage-container" style="
      position: relative;
      width: ${width}px;
      height: ${height}px;
      background-color: ${backgroundColor};
    ">
      ${cellsHTML}
    </div>
  `
}

/**
 * 在页面上创建隐藏的拼图元素
 */
export function createCollageElement(config: CollageConfig): HTMLElement {
  const html = createCollageHTML(config)
  
  // 创建隐藏容器
  const container = document.createElement('div')
  container.style.cssText = 'position: fixed; left: -9999px; top: -9999px;'
  container.innerHTML = html
  document.body.appendChild(container)
  
  return container.firstElementChild as HTMLElement
}

/**
 * 生成拼图并返回 Base64
 */
export async function generateCollage(config: CollageConfig): Promise<string> {
  const { options = {} } = config
  const { quality = 0.95, format = 'jpeg' } = options

  // 等待图片加载
  await preloadImages(config.images)

  // 创建拼图元素
  const element = createCollageElement(config)

  try {
    // 等待一下确保渲染完成
    await new Promise(resolve => setTimeout(resolve, 100))

    // 导出为图片
    const exportFn = format === 'png' ? toPng : toJpeg
    const dataUrl = await exportFn(element, { 
      quality,
      cacheBust: true,
    })

    console.log('✅ 拼图生成成功')
    return dataUrl
  } finally {
    // 清理 DOM
    element.parentElement?.remove()
  }
}

/**
 * 生成拼图并返回 File 对象
 */
export async function generateCollageFile(
  config: CollageConfig, 
  filename: string
): Promise<File> {
  const dataUrl = await generateCollage(config)
  return dataUrlToFile(dataUrl, filename)
}

// ============================================================
// 辅助函数
// ============================================================

/**
 * 预加载图片
 */
export async function preloadImages(urls: string[]): Promise<HTMLImageElement[]> {
  const promises = urls.map((url, index) => {
    return new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      
      img.onload = () => {
        console.log(`✅ [Preload ${index + 1}/${urls.length}] 成功:`, url.slice(0, 80))
        resolve(img)
      }
      
      img.onerror = (e) => {
        // 尝试检测 CORS 错误
        const isCORSError = e instanceof Event && e.type === 'error'
        const errorMsg = isCORSError 
          ? `CORS 错误：无法加载跨域图片。请检查 R2 存储桶的 CORS 设置是否允许当前域名（${window.location.origin}）访问。`
          : `图片加载失败`
        
        console.error(`❌ [Preload ${index + 1}/${urls.length}] ${errorMsg}`, url)
        reject(new Error(`${errorMsg}\n图片URL: ${url}`))
      }
      
      // 添加时间戳避开缓存，强制触发 CORS 验证
      const cacheBuster = `${url}${url.includes('?') ? '&' : '?'}t=${Date.now()}`
      img.src = cacheBuster
    })
  })
  
  return Promise.all(promises)
}

/**
 * 将 DOM 元素导出为图片
 */
export async function exportElementAsImage(
  element: HTMLElement,
  options: CollageOptions = {}
): Promise<string> {
  const { quality = 0.95, format = 'png' } = options

  try {
    const exportFn = format === 'jpeg' ? toJpeg : toPng
    const dataUrl = await exportFn(element, { quality })
    console.log('✅ 图片导出成功')
    return dataUrl
  } catch (error) {
    console.error('❌ 图片导出失败:', error)
    throw error
  }
}

/**
 * 下载图片到本地
 */
export function downloadImage(dataUrl: string, filename: string): void {
  const link = document.createElement('a')
  link.download = filename
  link.href = dataUrl
  link.click()
}

/**
 * 将 Base64 数据转换为 File 对象
 */
export function dataUrlToFile(dataUrl: string, filename: string): File {
  const arr = dataUrl.split(',')
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png'
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  return new File([u8arr], filename, { type: mime })
}

/**
 * 将 Base64 数据转换为 Blob
 */
export function dataUrlToBlob(dataUrl: string): Blob {
  const arr = dataUrl.split(',')
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png'
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  return new Blob([u8arr], { type: mime })
}

/**
 * 测试图片 URL 的 CORS 配置是否正确
 * 用于诊断跨域问题
 */
export async function testCORS(imageUrl: string): Promise<{ success: boolean; error?: string }> {
  return new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    
    const timeout = setTimeout(() => {
      resolve({ success: false, error: '加载超时（10秒）' })
    }, 10000)
    
    img.onload = () => {
      clearTimeout(timeout)
      
      // 尝试将图片绘制到 Canvas 以测试是否真的能跨域访问
      try {
        const canvas = document.createElement('canvas')
        canvas.width = img.width
        canvas.height = img.height
        const ctx = canvas.getContext('2d')
        ctx?.drawImage(img, 0, 0)
        
        // 尝试读取像素数据（如果 CORS 有问题会抛出错误）
        ctx?.getImageData(0, 0, 1, 1)
        
        resolve({ success: true })
      } catch (err) {
        resolve({ 
          success: false, 
          error: `图片加载成功但无法在 Canvas 中使用（CORS 错误）: ${err instanceof Error ? err.message : String(err)}` 
        })
      }
    }
    
    img.onerror = () => {
      clearTimeout(timeout)
      resolve({ 
        success: false, 
        error: `图片加载失败。可能原因：\n1. CORS 配置未允许 ${window.location.origin}\n2. 图片 URL 无效\n3. 网络问题` 
      })
    }
    
    img.src = imageUrl
  })
}
